import { Container } from 'unstated';
import moment from 'moment-timezone';
import * as api from '../../../../../shared/api/controllers/outboundCall';
import { ManagedOutboundCall, OutboundManagementQuery, OutboundManagementResult, OutboundDeletionRequest, OutboundDeletionPreview } from '../../../../../shared/models/outbound/OutboundCallManagement';

export const MAX_CALL_SELECTION = 20000;
export type SchedulePeriod = 'today' | 'tomorrow' | 'week' | 'month' | 'quarter' | 'custom';
interface ScheduledCallsState {
    query: OutboundManagementQuery; period: SchedulePeriod; data?: OutboundManagementResult;
    loading: boolean; busy: boolean; error: string; notice: string; selected: string[];
    deletion?: OutboundDeletionPreview; deletionLabel: string; deletingBatch: boolean; searchDraft: string; batchLabel: string;
}
export default class ScheduledCallsContainer extends Container<ScheduledCallsState> {
    state: ScheduledCallsState;
    private version = 0;
    constructor(private organizationId: string, private applicationId: string) {
        super();
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const today = moment.tz(timeZone).format('YYYY-MM-DD');
        this.state = { query: { startDate: today, endDate: moment.tz(timeZone).add(29, 'days').format('YYYY-MM-DD'),
            timeZone, view: 'upcoming', callType: '', search: '', batchId: '', groupByBatch: false, page: 1, pageSize: 25 },
            period: 'month', loading: true, busy: false, error: '', notice: '', selected: [], deletionLabel: '', deletingBatch: false, searchDraft: '', batchLabel: '' };
    }
    update = (changes: Partial<ScheduledCallsState>) => this.setState({ ...this.state, ...changes });
    private message = (result: any, fallback: string) => result?.httpStatus === 404 || result?.resultType === 'NotFound'
        ? 'This API does not support the new scheduled-call workspace yet. Run or deploy the updated Management API, then refresh.'
        : (result?.errors || []).map((e: any) => typeof e === 'string' ? e : e.message || '').filter(Boolean).join(' · ') || fallback;
    get rangeError(): string {
        const start = moment(this.state.query.startDate, 'YYYY-MM-DD', true);
        const end = moment(this.state.query.endDate, 'YYYY-MM-DD', true);
        return !start.isValid() || !end.isValid() || end.isBefore(start) || end.diff(start, 'days') > 92 ? 'Choose a date range of up to 93 days, with the end on or after the start.' : '';
    }
    load = async (preserveSelection: boolean = false) => {
        const version = ++this.version;
        if (this.rangeError) { await this.update({ loading: false, data: undefined, error: this.rangeError }); return; }
        await this.update({ loading: true, error: '', ...(preserveSelection ? {} : { selected: [] }) });
        try {
            const result = await api.getManagedOutboundCalls(this.organizationId, this.applicationId, this.state.query);
            if (version !== this.version) return;
            if (result.resultType !== 'Ok' || !result.data) throw new Error(this.message(result, 'Unable to load calls. Try refreshing.'));
            const lastPage = Math.max(1, Math.ceil(result.data.total / this.state.query.pageSize));
            if (this.state.query.page > lastPage) {
                await this.update({ query: { ...this.state.query, page: lastPage } });
                await this.load(preserveSelection);
                return;
            }
            await this.update({ data: result.data });
        } catch (e) { if (version === this.version) await this.update({ data: undefined, error: e.message }); }
        finally { if (version === this.version) await this.update({ loading: false }); }
    };
    change = async (changes: Partial<OutboundManagementQuery>) => {
        const paginationOnly = Object.keys(changes).every(key => key === 'page' || key === 'pageSize');
        await this.update({ query: { ...this.state.query, ...changes, page: changes.page || 1 }, ...(paginationOnly ? {} : { selected: [] }), notice: '' });
        await this.load(paginationOnly);
    };
    setPeriod = async (period: SchedulePeriod, view = this.state.query.view, timeZone = this.state.query.timeZone) => {
        const today = moment.tz(timeZone).startOf('day');
        let start = today.clone(), end = today.clone();
        if (period === 'tomorrow') { start.add(1, 'day'); end.add(1, 'day'); }
        if (['week', 'month', 'quarter'].includes(period)) {
            const days = period === 'week' ? 6 : period === 'month' ? 29 : 89;
            if (view === 'history') start.subtract(days, 'days'); else end.add(days, 'days');
        }
        await this.update({ period });
        await this.change({ view, timeZone, startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') });
    };
    setView = async (view: 'upcoming' | 'history') => {
        await this.update({ batchLabel: '', query: { ...this.state.query, batchId: '' } });
        await this.setPeriod('month', view);
    };
    setTimeZone = async (timeZone: string) => {
        if (this.state.period === 'custom') await this.change({ timeZone });
        else await this.setPeriod(this.state.period, this.state.query.view, timeZone);
    };
    search = () => this.change({ search: this.state.searchDraft.trim() });
    private select = (ids: string[]) => ids.length > MAX_CALL_SELECTION
        ? this.update({ notice: 'Select up to 20,000 calls at a time. Narrow the date range or call type, or remove the current selection first.' })
        : this.update({ selected: ids });
    toggle = (id: string) => this.select(this.state.selected.includes(id) ? this.state.selected.filter(item => item !== id) : [...this.state.selected, id]);
    togglePage = () => {
        const ids = (this.state.data?.calls || []).filter(c => c.canDelete).map(c => c.id);
        const all = ids.length > 0 && ids.every(id => this.state.selected.includes(id));
        return this.select(all ? this.state.selected.filter(id => !ids.includes(id)) : Array.from(new Set([...this.state.selected, ...ids])));
    };
    previewDeletion = async (request: OutboundDeletionRequest, label: string, selectOnly = false) => {
        if (this.state.busy) return;
        if (request.ids && request.ids.length > MAX_CALL_SELECTION) { await this.select(request.ids); return; }
        await this.update({ busy: true, error: '', notice: '' });
        try {
            const result = await api.previewOutboundDeletion(this.organizationId, this.applicationId, request);
            if (result.resultType !== 'Ok' || !result.data) throw new Error(this.message(result, 'Unable to review this deletion. Refresh and try again.'));
            if (result.data.ids.length > MAX_CALL_SELECTION) { await this.select(result.data.ids); return; }
            if (!result.data.ids.length) { await this.update({ notice: 'There are no remaining scheduled calls to delete. Queued and completed calls are protected.' }); return; }
            if (selectOnly) await this.update({ selected: result.data.ids });
            else await this.update({ deletion: result.data, deletionLabel: label, deletingBatch: request.scope === 'batch' });
        } catch (e) { await this.update({ error: e.message }); }
        finally { await this.update({ busy: false }); }
    };
    selectAllMatching = () => this.previewDeletion({ scope: 'filtered', query: this.state.query }, '', true);
    deleteSelected = () => this.previewDeletion({ scope: 'selected', ids: this.state.selected }, 'selected calls');
    deleteBatch = (id: string, name: string) => this.previewDeletion({ scope: 'batch', batchId: id }, name || 'Untitled upload');
    confirmDeletion = async () => {
        if (!this.state.deletion || this.state.busy) return;
        const ids = this.state.deletion.ids;
        await this.update({ busy: true, error: '' });
        try {
            const result = await api.deleteManagedOutboundCalls(this.organizationId, this.applicationId, ids);
            if (result.resultType !== 'Ok' || !result.data) throw new Error(this.message(result, 'Unable to delete calls. Retry to check their current state.'));
            const notice = `${result.data.deletedCount.toLocaleString()} scheduled calls deleted.${result.data.skippedCount ? ` ${result.data.skippedCount} were already removed or queued and were left unchanged.` : ''}`;
            await this.update({ deletion: undefined, selected: [], notice });
            await this.load();
        } catch (e) { await this.update({ error: e.message }); }
        finally { await this.update({ busy: false }); }
    };
    // With the upload's call dates, widen the upcoming range to contain them (the API allows at most 93 days).
    viewBatch = async (id: string, name: string, firstDate?: string, lastDate?: string) => {
        const query = this.state.query;
        const range: Partial<OutboundManagementQuery> = {};
        if (query.view === 'upcoming' && lastDate && lastDate > query.endDate) {
            const today = moment.tz(query.timeZone).format('YYYY-MM-DD');
            const start = moment(lastDate).diff(moment(today), 'days') <= 92 ? today : (firstDate && firstDate > today ? firstDate : today);
            const latest = moment(start).add(92, 'days').format('YYYY-MM-DD');
            Object.assign(range, { startDate: start, endDate: lastDate < latest ? lastDate : latest });
            await this.update({ period: 'custom' });
        }
        await this.update({ batchLabel: name });
        await this.change({ ...range, batchId: id, groupByBatch: false });
    };
    clearBatch = async () => { await this.update({ batchLabel: '' }); await this.change({ batchId: '' }); };
}
